import { Landmark, HeartHandshake, Users, Scale, Megaphone, ShieldCheck, Calendar, Target, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import {
  PageHero,
  Prose,
  SectionHeading,
  IconCardGrid,
  CtaBand,
  RelatedLinks,
  mailto,
} from "@/components/site/PageParts";
import { Reveal } from "@/components/site/Reveal";
import { Button } from "@/components/ui/button";
import { ASSETS } from "@/data/site";
import { Seo } from "@/components/site/Seo";

const SKILLS = [
  { Icon: Landmark, title: "Finance", body: "Help oversee budgets and financial health." },
  { Icon: HeartHandshake, title: "Fundraising", body: "Grow income and build supporter relationships." },
  { Icon: Users, title: "HR", body: "Support our people, volunteers and good practice." },
  { Icon: Scale, title: "Legal", body: "Guide governance and compliance." },
  { Icon: Megaphone, title: "Marketing", body: "Raise our profile and tell our story." },
  { Icon: ShieldCheck, title: "Safeguarding", body: "Champion the safety and welfare of our riders." },
];

const INVOLVED = [
  { Icon: Calendar, title: "Attend board meetings", body: "Join around four board meetings a year." }, // TODO: verify with RDA — number of board meetings
  { Icon: Target, title: "Contribute to strategy", body: "Help shape the direction and future of the charity." },
  { Icon: Sparkles, title: "Champion the charity", body: "Be an ambassador for RDA Glasgow in the community." },
];

export default function Vacancies() {
  return (
    <div data-testid="vacancies-page">
      <Seo title="Vacancies — RDA Glasgow" description="RDA Glasgow is recruiting new charity trustees to help govern the charity and shape the future of therapeutic riding in Greater Glasgow." image={ASSETS.facilityExterior} />
      <PageHero
        title="Charity Trustees Required"
        subtitle="Help govern RDA Glasgow and shape the future of therapeutic riding in Greater Glasgow."
        image={ASSETS.facilityExterior}
        imageAlt="The RDA Glasgow centre in Summerston"
        accent="brown"
      />

      <Prose>
        <SectionHeading eyebrow="Vacancies" title="We're recruiting new trustees" />
        <p>
          RDA Glasgow is recruiting new trustees to help govern the charity and guide our
          work. Trustees play a vital role — setting our direction, safeguarding our
          riders and horses, and making sure we can keep providing therapeutic riding for
          years to come.
        </p>
        <p>
          We welcome people from all walks of life. Skills we'd especially value include
          finance, fundraising, HR, legal, marketing, safeguarding, equestrian knowledge
          and disability sector experience — but above all, we're looking for people who
          care about our cause.
        </p>
      </Prose>

      {/* Skills welcomed */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="Skills welcomed" title="Could you bring one of these?" />
          <div className="mt-10">
            <IconCardGrid cards={SKILLS} cols={3} />
          </div>
        </div>
      </section>

      {/* What's involved */}
      <section className="bg-rda-offwhite">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="What's involved" title="The trustee role" />
          <div className="mt-10">
            <IconCardGrid cards={INVOLVED} cols={3} />
          </div>
        </div>
      </section>

      <CtaBand
        title="Interested in becoming a trustee?"
        subtitle="We'd love to hear from you. Send us a note about yourself and your interest."
        action={
          <Button
            asChild
            size="lg"
            className="rounded-full bg-white text-rda-tealDark hover:bg-white/90 font-semibold px-7 h-12"
            data-testid="trustee-apply-cta"
          >
            <a href={mailto("Trustee application")}>Apply to be a trustee</a>
          </Button>
        }
        testId="vacancies-cta-band"
      />

      {/* Other volunteer opportunities */}
      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14 text-center">
          <Reveal>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-rda-ink">
              Not a trustee role?
            </h2>
            <p className="mt-3 text-rda-muted">
              There are lots of other ways to get involved and give your time.
            </p>
            <Button
              asChild
              variant="outline"
              className="mt-6 rounded-full border-rda-teal text-rda-teal hover:bg-rda-teal hover:text-white font-semibold"
              data-testid="vacancies-volunteering-link"
            >
              <Link to="/volunteering">See volunteer roles</Link>
            </Button>
          </Reveal>
        </div>
      </section>

      <RelatedLinks
        links={[
          { title: "Volunteering", description: "Hands-on roles that make sessions possible.", to: "/volunteering" },
          { title: "About Us", description: "Learn about the charity you'd help govern.", to: "/about" },
          { title: "Support Us", description: "Other ways to support our work.", to: "/support-us" },
        ]}
      />
    </div>
  );
}
